import {
  useCallback,
  useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { mapSettingsDataLayersUpdateDataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import { useMapSettingsDataLayersInstancesSelector } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import type { DataLayerId } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import {
  createSampleBTGForDataLayer, DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES,
} from './dataLayersTool.helpers';

export const useDataLayersEditViaBTG = (dataLayerId: DataLayerId) => {
  const dispatch = useDispatch();
  const dataLayers = useMapSettingsDataLayersInstancesSelector();

  const dataLayer = dataLayers.find(layer => layer.id === dataLayerId);
  const initialSampleBTG = useMemo(() => dataLayer ? createSampleBTGForDataLayer({ dataLayer }) : null, [dataLayer]);
  const [boundaryTerritoryGroup, setBoundaryTerritoryGroup] = useState(initialSampleBTG);

  const onNumberOfRangesChange = useCallback((newNumberOfRanges: number) => {
    if (boundaryTerritoryGroup) {
      setBoundaryTerritoryGroup({
        ...boundaryTerritoryGroup,
        settings: {
          ...boundaryTerritoryGroup.settings,
          rangesCount: newNumberOfRanges,
          // also recalculate boundary territories if needed to display them
        },
      });
    }
  }, [boundaryTerritoryGroup]);

  useEffect(() => {
    if (dataLayer) {
      // data layer changed outside, so we update BTG discarding any active changes
      setBoundaryTerritoryGroup(createSampleBTGForDataLayer({
        dataLayer,
      }));
    }
  }, [dataLayer]);

  const onSubmit = useCallback(() => {
    if (boundaryTerritoryGroup) {
      dispatch(mapSettingsDataLayersUpdateDataLayer({
        id: dataLayerId,
        settings: {
          style: boundaryTerritoryGroup.settings.style,
          numberOfRanges: boundaryTerritoryGroup.settings.rangesCount || DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES,
          disableMapInteraction: boundaryTerritoryGroup.settings.disableMapInteraction,
          hideLabels: boundaryTerritoryGroup.settings.hideLabels,
          labelStyle: boundaryTerritoryGroup.settings.labelStyle,
          labelValueConfig: boundaryTerritoryGroup.settings.labelValueConfig,
        },
      }));
    }
  }, [dataLayerId, dispatch, boundaryTerritoryGroup]);

  return {
    dataLayer,
    boundaryTerritoryGroup,
    updateBoundaryTerritoryGroup: setBoundaryTerritoryGroup,
    onNumberOfRangesChange,
    onSubmit,
  };
};
